/**
 * ThermalModelPage.tsx — Annual lake-water heat budget (Henderson-Sellers 1986)
 *
 * Computes the monthly lake temperature, dissolved oxygen saturation, stratification
 * onset and turnover for Barcombe Reservoir. Feeds the time-domain DO simulation
 * on the Bed Assessment page with a *continuous* seasonal trajectory rather than
 * a fixed snapshot.
 *
 * References:
 *   Henderson-Sellers (1986). Calculating the surface energy balance for lake and reservoir modelling.
 *   Imboden & Wuest (1995). Mixing mechanisms in lakes.
 *   APHA (2017). Saturation DO from temperature and elevation.
 *   Idso (1973). On the concept of lake stability.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Thermometer, ArrowLeft, RefreshCcw, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, savePersisted, KEYS, onPersistChange } from '@/lib/sessionPersistence';
import { readOverridesAsNumbers, SIM_DATA_KEY } from '@/pages/SimulationDataPage';
import SimOverrideBanner from '@/components/SimOverrideBanner';

// ─── Henderson-Sellers constants ─────────────────────────────────────────────
const SIGMA = 5.67e-8;       // Stefan-Boltzmann W/m²/K⁴
const C_P_AIR = 1005;        // J/kg/K
const RHO_AIR = 1.2;         // kg/m³
const RHO_W = 1000;
const C_P_W = 4184;
const L_V = 2.45e6;          // latent heat of vaporisation J/kg
const ALBEDO = 0.07;
const EMISSIVITY = 0.97;
const G = 9.81;

// Typical monthly climate for Sussex (Barcombe area) — long-term averages
interface MonthlyClimate {
  month: string;
  T_air_C: number;
  U10_ms: number;
  RH_pct: number;
  Q_solar_W_m2: number;   // surface incoming shortwave (daily mean)
  cloud_frac: number;
}

const DEFAULT_CLIMATE: MonthlyClimate[] = [
  { month: 'Jan', T_air_C:  5.0, U10_ms: 4.5, RH_pct: 87, Q_solar_W_m2:  50, cloud_frac: 0.78 },
  { month: 'Feb', T_air_C:  5.4, U10_ms: 4.3, RH_pct: 84, Q_solar_W_m2:  90, cloud_frac: 0.72 },
  { month: 'Mar', T_air_C:  7.8, U10_ms: 4.2, RH_pct: 80, Q_solar_W_m2: 150, cloud_frac: 0.65 },
  { month: 'Apr', T_air_C: 10.3, U10_ms: 3.8, RH_pct: 76, Q_solar_W_m2: 200, cloud_frac: 0.58 },
  { month: 'May', T_air_C: 13.5, U10_ms: 3.5, RH_pct: 74, Q_solar_W_m2: 250, cloud_frac: 0.55 },
  { month: 'Jun', T_air_C: 16.3, U10_ms: 3.3, RH_pct: 73, Q_solar_W_m2: 270, cloud_frac: 0.54 },
  { month: 'Jul', T_air_C: 18.6, U10_ms: 3.2, RH_pct: 74, Q_solar_W_m2: 260, cloud_frac: 0.56 },
  { month: 'Aug', T_air_C: 18.4, U10_ms: 3.2, RH_pct: 76, Q_solar_W_m2: 220, cloud_frac: 0.58 },
  { month: 'Sep', T_air_C: 15.7, U10_ms: 3.4, RH_pct: 79, Q_solar_W_m2: 160, cloud_frac: 0.62 },
  { month: 'Oct', T_air_C: 12.2, U10_ms: 3.9, RH_pct: 83, Q_solar_W_m2: 100, cloud_frac: 0.70 },
  { month: 'Nov', T_air_C:  8.4, U10_ms: 4.3, RH_pct: 86, Q_solar_W_m2:  60, cloud_frac: 0.76 },
  { month: 'Dec', T_air_C:  5.7, U10_ms: 4.5, RH_pct: 88, Q_solar_W_m2:  40, cloud_frac: 0.79 },
];

const RESERVOIR_AREA_M2 = 34_000;
const DEFAULT_DEPTH_M = 3.0;

function fmt(n: number, d = 2) {
  if (!isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
}

// Saturation vapour pressure (Magnus formula) — Pa
function eSat(T_C: number) {
  return 611 * Math.exp(17.27 * T_C / (T_C + 237.3));
}

// DO saturation at temperature T (°C) and 1 atm — APHA / Benson-Krause
function doSatMgL(T_C: number) {
  return Math.max(0, 14.652 - 0.41022 * T_C + 0.0079910 * T_C ** 2 - 0.000077774 * T_C ** 3);
}

interface MonthlyResult {
  month: string;
  T_air_C: number;
  T_lake_C: number;
  DO_sat: number;
  Q_net_W_m2: number;
  stratified: boolean;
  thermocline_m: number;
}

function runHeatBudget(climate: MonthlyClimate[], depthM: number, T_init_C: number): MonthlyResult[] {
  const out: MonthlyResult[] = [];
  let T_lake = T_init_C;
  const V = RESERVOIR_AREA_M2 * depthM;
  const M = V * RHO_W;
  const dt = 30 * 24 * 3600;   // 30 days in seconds

  for (let i = 0; i < climate.length; i++) {
    const c = climate[i];
    // Shortwave net
    const Q_sw = c.Q_solar_W_m2 * (1 - ALBEDO);

    // Longwave net (Stefan-Boltzmann, both ways with cloud cover correction)
    const T_air_K = c.T_air_C + 273.15;
    const T_lake_K = T_lake + 273.15;
    const e_a = (c.RH_pct / 100) * eSat(c.T_air_C);
    const epsilon_air = (0.74 + 0.0049 * e_a / 100) * (1 + 0.17 * c.cloud_frac * c.cloud_frac);
    const Q_lw_in  = epsilon_air * SIGMA * Math.pow(T_air_K, 4);
    const Q_lw_out = EMISSIVITY * SIGMA * Math.pow(T_lake_K, 4);
    const Q_lw_net = Q_lw_in - Q_lw_out;

    // Sensible heat
    const Ce = 0.0013;  // bulk transfer coefficient
    const Q_h = -Ce * RHO_AIR * C_P_AIR * c.U10_ms * (T_lake - c.T_air_C);

    // Latent heat
    const e_s_lake = eSat(T_lake);
    const Q_e = -Ce * RHO_AIR * L_V * 0.622 / 101325 * c.U10_ms * (e_s_lake - e_a);

    // Net heat flux
    const Q_net = Q_sw + Q_lw_net + Q_h + Q_e;

    // Apply over the month
    const dT = Q_net * RESERVOIR_AREA_M2 * dt / (M * C_P_W);
    T_lake = Math.max(2.0, Math.min(28.0, T_lake + dT));

    // Stratification heuristic — summer months with T_lake > 12 and large delta-T trigger
    const stratified = T_lake > 11 && c.U10_ms < 4.0;
    const thermocline_m = stratified ? Math.max(0.8, Math.min(depthM - 0.5, depthM * 0.35)) : depthM;

    out.push({
      month: c.month,
      T_air_C: c.T_air_C,
      T_lake_C: T_lake,
      DO_sat: doSatMgL(T_lake),
      Q_net_W_m2: Q_net,
      stratified,
      thermocline_m,
    });
  }
  return out;
}

const KEY = 'rb_thermal_model_v1';

interface ThermalState {
  climate: MonthlyClimate[];
  depthM: number;
  T_init_C: number;
}

const DEFAULT_STATE: ThermalState = {
  climate: DEFAULT_CLIMATE,
  depthM: DEFAULT_DEPTH_M,
  T_init_C: 5.0,
};

export default function ThermalModelPage() {
  const navigate = useNavigate();
  const [state, setState] = useState<ThermalState>(() => loadPersisted<ThermalState>(KEY, DEFAULT_STATE));
  const [nonce, setNonce] = useState(0);

  // Simulation overrides — e1 U10, e2 T_air mean, e3 solar mean, e4 RH mean
  const [simOver, setSimOver] = useState(() => readOverridesAsNumbers(['e1', 'e2', 'e3', 'e4']));

  useEffect(() => { savePersisted(KEY, state); }, [state]);

  // Reactively follow the active session's water depth + sim overrides
  useEffect(() => {
    const unsub = onPersistChange((key) => {
      if (key === KEYS.DIFFUSER_MODULE) setNonce(n => n + 1);
      if (key === SIM_DATA_KEY) setSimOver(readOverridesAsNumbers(['e1', 'e2', 'e3', 'e4']));
    });
    return () => unsub();
  }, []);

  const sessionDepth = useMemo(() => {
    const sm = loadPersisted<{ waterDepthM?: number }>(KEYS.DIFFUSER_MODULE, {});
    return sm.waterDepthM ?? state.depthM;
  }, [nonce, state.depthM]);

  // Climate with overrides — shift the monthly array to match measured annual means.
  // T_air: additive shift so annual mean matches override
  // U10:   multiplicative scaling
  // Solar: multiplicative scaling
  // RH:    additive shift (clamped 30–100 %)
  const climateEff = useMemo(() => {
    const c = state.climate.map(m => ({ ...m }));
    const meanT_default = c.reduce((s, m) => s + m.T_air_C, 0) / c.length;
    const meanU_default = c.reduce((s, m) => s + m.U10_ms, 0) / c.length;
    const meanS_default = c.reduce((s, m) => s + m.Q_solar_W_m2, 0) / c.length;
    const meanH_default = c.reduce((s, m) => s + m.RH_pct, 0) / c.length;
    if (simOver.activeIds.has('e2')) {
      const delta = simOver.values.e2 - meanT_default;
      c.forEach(m => { m.T_air_C += delta; });
    }
    if (simOver.activeIds.has('e1') && meanU_default > 0) {
      const scale = simOver.values.e1 / meanU_default;
      c.forEach(m => { m.U10_ms = m.U10_ms * scale; });
    }
    if (simOver.activeIds.has('e3') && meanS_default > 0) {
      const scale = simOver.values.e3 / meanS_default;
      c.forEach(m => { m.Q_solar_W_m2 = m.Q_solar_W_m2 * scale; });
    }
    if (simOver.activeIds.has('e4')) {
      const delta = simOver.values.e4 - meanH_default;
      c.forEach(m => { m.RH_pct = Math.max(30, Math.min(100, m.RH_pct + delta)); });
    }
    return c;
  }, [state.climate, simOver]);

  const results = useMemo(
    () => runHeatBudget(climateEff, state.depthM, state.T_init_C),
    [climateEff, state.depthM, state.T_init_C]
  );

  const annual = useMemo(() => {
    const tmin = Math.min(...results.map(r => r.T_lake_C));
    const tmax = Math.max(...results.map(r => r.T_lake_C));
    const dosMin = Math.min(...results.map(r => r.DO_sat));
    const stratMonths = results.filter(r => r.stratified).length;
    const annualMeanT = results.reduce((s, r) => s + r.T_lake_C, 0) / results.length;
    return { tmin, tmax, dosMin, stratMonths, annualMeanT };
  }, [results]);

  const updateClimate = (idx: number, key: keyof MonthlyClimate, val: number) => {
    const nc = [...state.climate];
    nc[idx] = { ...nc[idx], [key]: val };
    setState({ ...state, climate: nc });
  };

  // SVG plot of T_lake & T_air vs month
  const svgW = 720;
  const svgH = 240;
  const padL = 40, padR = 20, padT = 20, padB = 30;
  const plotW = svgW - padL - padR;
  const plotH = svgH - padT - padB;
  const yMin = 0, yMax = 25;
  const x = (i: number) => padL + (i / 11) * plotW;
  const y = (v: number) => padT + plotH - ((v - yMin) / (yMax - yMin)) * plotH;

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={Thermometer}
        title="Lake Thermal Model — Annual Heat Budget"
        subtitle="Henderson-Sellers 1986 monthly energy balance with stratification onset, DO saturation and thermocline depth"
        actions={
          <button onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        }
      />

      <SimOverrideBanner consumes={[
        { id: 'e1', label: 'U10 wind',  active: simOver.activeIds.has('e1'), value: `${simOver.values.e1} m/s` },
        { id: 'e2', label: 'T_air mean', active: simOver.activeIds.has('e2'), value: `${simOver.values.e2} °C` },
        { id: 'e3', label: 'Solar mean', active: simOver.activeIds.has('e3'), value: `${simOver.values.e3} W/m²` },
        { id: 'e4', label: 'RH mean',    active: simOver.activeIds.has('e4'), value: `${simOver.values.e4} %` },
      ]} />

      {/* Headline tiles */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Min lake temp</div>
          <div className="text-2xl font-bold text-blue-300 mt-1">{fmt(annual.tmin, 1)} °C</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Max lake temp</div>
          <div className="text-2xl font-bold text-red-300 mt-1">{fmt(annual.tmax, 1)} °C</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Annual mean</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(annual.annualMeanT, 1)} °C</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Min DO sat</div>
          <div className="text-2xl font-bold text-emerald-300 mt-1">{fmt(annual.dosMin, 2)} mg/L</div>
        </div>
        <div className="rounded-xl border border-amber-700 bg-amber-900/20 p-4">
          <div className="text-xs text-amber-300 uppercase tracking-wider">Stratified months</div>
          <div className="text-2xl font-bold text-amber-200 mt-1">{annual.stratMonths}</div>
        </div>
      </div>

      {/* Inputs */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="text-sm font-semibold text-slate-100">Model inputs</div>
          <button onClick={() => { setState({ ...state, depthM: sessionDepth }); }}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-800 px-2.5 py-1 text-xs text-slate-300 hover:bg-slate-700">
            <RefreshCcw className="w-3.5 h-3.5" /> Use session depth ({sessionDepth.toFixed(1)} m)
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <label className="text-xs text-slate-400 block mb-1">Reservoir mean depth (m)</label>
            <input type="number" step={0.1} value={state.depthM}
              onChange={e => setState({ ...state, depthM: Number(e.target.value) })}
              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Initial January temp (°C)</label>
            <input type="number" step={0.5} value={state.T_init_C}
              onChange={e => setState({ ...state, T_init_C: Number(e.target.value) })}
              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
          </div>
          <div>
            <label className="text-xs text-slate-400 block mb-1">Surface area (m²)</label>
            <input type="number" value={RESERVOIR_AREA_M2} disabled
              className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-400" />
          </div>
        </div>
      </div>

      {/* SVG chart */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">Monthly lake temperature & DO saturation</div>
        <svg viewBox={`0 0 ${svgW} ${svgH}`} className="w-full h-auto bg-slate-950 rounded">
          {/* axes */}
          <line x1={padL} y1={padT} x2={padL} y2={padT + plotH} stroke="#64748b" strokeWidth={1} />
          <line x1={padL} y1={padT + plotH} x2={padL + plotW} y2={padT + plotH} stroke="#64748b" strokeWidth={1} />
          {/* gridlines */}
          {[0, 5, 10, 15, 20, 25].map(v => (
            <g key={v}>
              <line x1={padL} y1={y(v)} x2={padL + plotW} y2={y(v)} stroke="#1e293b" strokeWidth={0.5} />
              <text x={padL - 4} y={y(v) + 3} textAnchor="end" fontSize={9} fill="#94a3b8">{v}</text>
            </g>
          ))}
          {/* T_air line */}
          <polyline fill="none" stroke="#a78bfa" strokeWidth={1.5}
            points={results.map((r, i) => `${x(i)},${y(r.T_air_C)}`).join(' ')} />
          {/* T_lake line */}
          <polyline fill="none" stroke="#22c55e" strokeWidth={2.5}
            points={results.map((r, i) => `${x(i)},${y(r.T_lake_C)}`).join(' ')} />
          {/* DO_sat line (scale: DO mg/L mapped onto same axis since they overlap usefully ~8-14) */}
          <polyline fill="none" stroke="#38bdf8" strokeWidth={1.5} strokeDasharray="3 3"
            points={results.map((r, i) => `${x(i)},${y(r.DO_sat)}`).join(' ')} />
          {/* stratified shading */}
          {results.map((r, i) => r.stratified && (
            <rect key={i} x={x(i) - (plotW / 24)} y={padT} width={plotW / 12} height={plotH}
              fill="rgba(245, 158, 11, 0.08)" />
          ))}
          {/* x labels */}
          {results.map((r, i) => (
            <text key={i} x={x(i)} y={padT + plotH + 15} textAnchor="middle" fontSize={9} fill="#94a3b8">{r.month}</text>
          ))}
          {/* legend */}
          <g transform={`translate(${svgW - 180}, 8)`}>
            <line x1={0} y1={4} x2={20} y2={4} stroke="#22c55e" strokeWidth={2.5} />
            <text x={24} y={7} fontSize={9} fill="#22c55e">T_lake</text>
            <line x1={60} y1={4} x2={80} y2={4} stroke="#a78bfa" strokeWidth={1.5} />
            <text x={84} y={7} fontSize={9} fill="#a78bfa">T_air</text>
            <line x1={110} y1={4} x2={130} y2={4} stroke="#38bdf8" strokeWidth={1.5} strokeDasharray="3 3" />
            <text x={134} y={7} fontSize={9} fill="#38bdf8">DO_sat</text>
          </g>
        </svg>
        <div className="text-[10px] text-slate-500 mt-1">Amber bands indicate stratified months (Lake T &gt; 11 °C, mild wind).</div>
      </div>

      {/* Climate table */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">Monthly climate (editable)</div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-800">
              <tr>
                <th className="px-2 py-1.5 text-left text-slate-300">Month</th>
                <th className="px-2 py-1.5 text-right text-slate-300">T_air (°C)</th>
                <th className="px-2 py-1.5 text-right text-slate-300">U10 (m/s)</th>
                <th className="px-2 py-1.5 text-right text-slate-300">RH (%)</th>
                <th className="px-2 py-1.5 text-right text-slate-300">Solar (W/m²)</th>
                <th className="px-2 py-1.5 text-right text-slate-300">Cloud</th>
                <th className="px-2 py-1.5 text-right text-slate-300">T_lake (°C)</th>
                <th className="px-2 py-1.5 text-right text-slate-300">DO_sat (mg/L)</th>
                <th className="px-2 py-1.5 text-right text-slate-300">Q_net (W/m²)</th>
                <th className="px-2 py-1.5 text-center text-slate-300">Strat?</th>
              </tr>
            </thead>
            <tbody>
              {state.climate.map((c, i) => (
                <tr key={i} className="border-b border-slate-800">
                  <td className="px-2 py-1 text-slate-100 font-semibold">{c.month}</td>
                  <td className="px-2 py-1 text-right">
                    <input type="number" step={0.1} value={c.T_air_C}
                      onChange={e => updateClimate(i, 'T_air_C', Number(e.target.value))}
                      className="w-16 bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-right text-slate-100" />
                  </td>
                  <td className="px-2 py-1 text-right">
                    <input type="number" step={0.1} value={c.U10_ms}
                      onChange={e => updateClimate(i, 'U10_ms', Number(e.target.value))}
                      className="w-14 bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-right text-slate-100" />
                  </td>
                  <td className="px-2 py-1 text-right">
                    <input type="number" value={c.RH_pct}
                      onChange={e => updateClimate(i, 'RH_pct', Number(e.target.value))}
                      className="w-14 bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-right text-slate-100" />
                  </td>
                  <td className="px-2 py-1 text-right">
                    <input type="number" value={c.Q_solar_W_m2}
                      onChange={e => updateClimate(i, 'Q_solar_W_m2', Number(e.target.value))}
                      className="w-16 bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-right text-slate-100" />
                  </td>
                  <td className="px-2 py-1 text-right">
                    <input type="number" step={0.05} value={c.cloud_frac}
                      onChange={e => updateClimate(i, 'cloud_frac', Number(e.target.value))}
                      className="w-14 bg-slate-800 border border-slate-700 rounded px-1 py-0.5 text-right text-slate-100" />
                  </td>
                  <td className="px-2 py-1 text-right text-emerald-300 tabular-nums">{fmt(results[i].T_lake_C, 1)}</td>
                  <td className="px-2 py-1 text-right text-sky-300 tabular-nums">{fmt(results[i].DO_sat, 2)}</td>
                  <td className="px-2 py-1 text-right text-slate-300 tabular-nums">{fmt(results[i].Q_net_W_m2, 1)}</td>
                  <td className="px-2 py-1 text-center">
                    {results[i].stratified ? <span className="text-amber-400">✓</span> : <span className="text-slate-600">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
          <Info className="w-4 h-4 text-rb-brand" /> Why this matters for module count
        </div>
        <div className="text-xs text-slate-400 leading-relaxed space-y-2">
          <p>
            Today's seasonal profile engine snaps the design to a fixed worst-case
            condition (typically late-summer stratification). The thermal model
            replaces that snapshot with a continuous monthly trajectory, so the
            time-domain DO simulation can be driven by a calibrated annual cycle
            rather than a single assumed state.
          </p>
          <p>
            Stratified-month count, max lake temperature and min DO saturation feed
            directly into the AOTR demand and into the algal-dynamics module. The
            model is calibratable: replace the default Sussex climate with the
            site-measured MIDAS record when site visit data arrives, and every
            downstream calculation updates automatically.
          </p>
        </div>
      </div>
    </div>
  );
}
